import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, FileText, Receipt, type LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Seccion } from '@/shared/ui/Seccion';
import { BarrasApiladas, enDinero, enPorcentaje } from '@/shared/ui/graficos';

export const CONCEPTOS = [
  { valor: '', etiqueta: 'Todos los conceptos' },
  { valor: 'ART9', etiqueta: 'Tasa del Art. 9' },
  { valor: 'ART8', etiqueta: 'Títulos del Art. 8' },
  { valor: 'GENERADORES', etiqueta: 'Generadores y servicios especiales' },
] as const;

interface Cifras {
  facturas: number;
  facturado: number;
  cobrado: number;
  pendiente: number;
  porcentaje: number;
}

interface Mes extends Cifras {
  periodo: string;
}

interface Zona extends Cifras {
  ruralidad: string;
  parroquia: string;
}

interface Mensual {
  totales: Cifras;
  meses: Mes[];
}

interface PorRuralidad {
  totales: Cifras;
  zonas: Zona[];
}

const SERIES = [
  { clave: 'cobrado', nombre: 'Cobrado', color: '#0E7C5A' },
  { clave: 'pendiente', nombre: 'Pendiente', color: '#F59E0B' },
] as const;

export function PanelDeResumen({
  desde,
  hasta,
  concepto,
}: {
  desde: string;
  hasta: string;
  concepto: string;
}) {
  const consulta = { desde, hasta, ...(concepto ? { concepto } : {}) };
  const mensual = useQuery({
    queryKey: ['informe-resumen', 'mensual', desde, hasta, concepto],
    queryFn: () =>
      api.get<Mensual>('/informes/facturacion/resumen/mensual/panel', { query: consulta }),
  });
  const ruralidad = useQuery({
    queryKey: ['informe-resumen', 'ruralidad', desde, hasta, concepto],
    queryFn: () =>
      api.get<PorRuralidad>('/informes/facturacion/resumen/ruralidad/panel', { query: consulta }),
  });

  if (mensual.isLoading) {
    return <Cargando titulo="Cargando" detalle="sumando lo facturado y lo cobrado" />;
  }

  const datos = mensual.data;
  if (!datos || datos.meses.length === 0) {
    return (
      <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
        No se facturó nada en esos meses
      </p>
    );
  }
  const t = datos.totales;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra
          icono={Receipt}
          rotulo="Facturado"
          valor={enDinero(t.facturado)}
          tono="text-armazon-700"
          fondo="bg-armazon-50"
        />
        <Cifra
          icono={CheckCircle2}
          rotulo="Cobrado"
          valor={`${enDinero(t.cobrado)} · ${enPorcentaje(t.porcentaje)}`}
          tono="text-marca-700"
          fondo="bg-marca-50"
        />
        <Cifra
          icono={Clock}
          rotulo="Pendiente"
          valor={enDinero(t.pendiente)}
          tono="text-amber-700"
          fondo="bg-amber-50"
        />
        <Cifra
          icono={FileText}
          rotulo="Facturas"
          valor={t.facturas.toLocaleString('es-EC')}
          tono="text-armazon-700"
          fondo="bg-armazon-50"
        />
      </div>

      <Seccion
        titulo="Cobrado y pendiente por mes de servicio"
        ayuda="El alto de la barra es lo facturado ese mes. Sin anuladas"
      >
        <BarrasApiladas datos={datos.meses} claveEtiqueta="periodo" series={SERIES} />
      </Seccion>

      <Seccion titulo="Resumen mensual" ayuda="Junta el sistema anterior y SIREPAGAL">
        <Tabla
          encabezado="Mes"
          filas={datos.meses.map((m) => ({ clave: m.periodo, etiqueta: m.periodo, cifras: m }))}
          total={t}
        />
      </Seccion>

      <Seccion
        titulo="Por ruralidad"
        ayuda="Por la parroquia del contribuyente. Sin sector: clientes que no la tienen puesta"
      >
        {ruralidad.isLoading || !ruralidad.data ? (
          <Cargando titulo="Cargando" detalle="repartiendo por parroquia" />
        ) : (
          <TablaDeZonas datos={ruralidad.data} />
        )}
      </Seccion>
    </div>
  );
}

function TablaDeZonas({ datos }: { datos: PorRuralidad }) {
  const grupos = [...new Set(datos.zonas.map((z) => z.ruralidad))];
  return (
    <Tabla
      encabezado="Parroquia"
      filas={grupos.flatMap((g) => {
        const suyas = datos.zonas.filter((z) => z.ruralidad === g);
        const sub = suyas.reduce(
          (a, z) => ({
            facturas: a.facturas + z.facturas,
            facturado: a.facturado + z.facturado,
            cobrado: a.cobrado + z.cobrado,
            pendiente: a.pendiente + z.pendiente,
            porcentaje: 0,
          }),
          { facturas: 0, facturado: 0, cobrado: 0, pendiente: 0, porcentaje: 0 },
        );
        sub.porcentaje = sub.facturado > 0 ? (sub.cobrado / sub.facturado) * 100 : 0;
        return [
          { clave: `g-${g}`, etiqueta: g, cifras: sub, grupo: true },
          ...suyas.map((z) => ({ clave: `${g}-${z.parroquia}`, etiqueta: z.parroquia, cifras: z })),
        ];
      })}
      total={datos.totales}
    />
  );
}

function Tabla({
  encabezado,
  filas,
  total,
}: {
  encabezado: string;
  filas: { clave: string; etiqueta: string; cifras: Cifras; grupo?: boolean }[];
  total: Cifras;
}) {
  const columnas = [encabezado, 'Facturas', 'Facturado', 'Cobrado', 'Pendiente', '% cobrado'];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-armazon-50/80">
          <tr>
            {columnas.map((c, i) => (
              <th
                key={c}
                scope="col"
                className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                  i === 0 ? 'text-left' : 'text-right'
                }`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-armazon-100">
          {filas.map((f) => (
            <tr
              key={f.clave}
              className={f.grupo ? 'bg-armazon-50/70' : 'transition-colors hover:bg-armazon-50/60'}
            >
              <td
                className={`px-3 py-2.5 text-xs uppercase ${f.grupo ? 'font-bold text-armazon-800' : 'pl-6 font-medium text-armazon-700'}`}
              >
                {f.etiqueta}
              </td>
              <Celdas cifras={f.cifras} fuerte={f.grupo ?? false} />
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-armazon-200 bg-armazon-50/80">
          <tr>
            <td className="px-3 py-2.5 text-xs font-bold uppercase text-armazon-800">Total</td>
            <Celdas cifras={total} fuerte />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Celdas({ cifras: c, fuerte }: { cifras: Cifras; fuerte: boolean }) {
  const clase = `px-3 py-2.5 text-right text-xs tabular-nums ${fuerte ? 'font-bold text-armazon-900' : ''}`;
  return (
    <>
      <td className={clase}>{c.facturas.toLocaleString('es-EC')}</td>
      <td className={clase}>{enDinero(c.facturado)}</td>
      <td className={`${clase} text-marca-700`}>{enDinero(c.cobrado)}</td>
      <td className={`${clase} text-amber-700`}>{enDinero(c.pendiente)}</td>
      <td className={clase}>{enPorcentaje(c.porcentaje)}</td>
    </>
  );
}

function Cifra({
  icono: Icono,
  rotulo,
  valor,
  tono,
  fondo,
}: {
  icono: LucideIcon;
  rotulo: string;
  valor: string;
  tono: string;
  fondo: string;
}) {
  return (
    <div className={`rounded-2xl border border-white/60 p-4 ${fondo}`}>
      <span className="mb-2 flex items-center gap-2">
        <Icono className={`size-4 ${tono}`} aria-hidden />
        <span className="text-[10px] font-bold uppercase tracking-wider text-armazon-500">
          {rotulo}
        </span>
      </span>
      <span className={`block truncate text-xl font-bold tabular-nums ${tono}`}>{valor}</span>
    </div>
  );
}
